import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trophy, Flame, CalendarDays, Share2, Sparkles, Sprout, ChevronRight, Lock, Star } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatMoney } from "@/lib/currency";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import GoalCompleteModal from "@/components/GoalCompleteModal";
import MonthlySnapshotModal from "@/components/MonthlySnapshotModal";
import YearInReviewModal from "@/components/YearInReviewModal";

/**
 * ShareHub — /share page. A curated gallery of the user's shareable
 * moments (completed goals, streak milestones, this-month's snapshot).
 * All wins are derived client-side from existing endpoints — zero
 * backend changes required for this page.
 */

const STREAK_TIERS = [
  { months: 12, name: "Legend",      icon: "trophy" },
  { months: 6,  name: "Disciplined", icon: "crown"  },
  { months: 3,  name: "Consistent",  icon: "medal"  },
];

export default function ShareHub() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const cur = user?.currency || "USD";

  const [goals, setGoals] = useState([]);
  const [streak, setStreak] = useState(null);
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedGoal, setSelectedGoal] = useState(null);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [g, st, s, ins, cf] = await Promise.all([
          api.get("/goals"),
          api.get("/streak"),
          api.get("/dashboard/summary"),
          api.get("/optimizer/insights"),
          api.get("/reports/cashflow?months=12").catch(() => ({ data: null })),
        ]);
        setGoals(g.data || []);
        setStreak(st.data);
        setSummary(s.data);
        setInsights(ins.data);
        // Normalise cashflow → {points:[{month_label, cashflow}]}
        const rows = Array.isArray(cf.data) ? cf.data : [];
        const monthLabel = (ym) => {
          const [y, m] = String(ym).split("-").map(Number);
          if (!y || !m) return ym;
          const d = new Date(y, m - 1, 1);
          return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
        };
        setCashflow({
          points: rows
            .filter(r => !r.forecast)
            .map(r => ({ month_label: monthLabel(r.month), cashflow: Number(r.net) || 0 })),
        });
      } catch (e) { console.error("ShareHub load failed:", e); }
      finally { setLoading(false); }
    })();
  }, []);

  // Derive the wins list — sorted with recent/big wins first
  const wins = useMemo(() => {
    const list = [];
    // 0) Year in review — always available (headline)
    if (summary) {
      const year = new Date().getFullYear();
      list.push({
        key: "year-in-review",
        type: "year",
        title: t("pages.sharehub.year_in_review", { year }),
        body: t("pages.sharehub.year_in_review_body"),
        icon: "star",
        tint: "gradient",
      });
    }
    // 1) Completed goals
    for (const g of goals) {
      const target = Number(g.target_amount) || 0;
      const saved  = Number(g.saved_amount)  || 0;
      if (target > 0 && saved >= target) {
        list.push({
          key: `goal-${g.id}`,
          type: "goal",
          title: g.name,
          body: t("pages.sharehub.saved_amount", { amount: formatMoney(target, cur) }),
          amount: target,
          icon: "trophy",
          tint: "amber",
          data: g,
        });
      }
    }
    // 2) Streak tier reached (highest applicable)
    const s = streak?.current_streak || 0;
    const tier = STREAK_TIERS.find(t => s >= t.months);
    if (tier) {
      list.push({
        key: `streak-${tier.months}`,
        type: "streak",
        title: t("pages.sharehub.streak_tier", { name: tier.name }),
        body: t("pages.sharehub.streak_body", { count: s }),
        icon: "flame",
        tint: "green",
      });
    }
    // 3) Current month snapshot — always available if there is any data
    if (summary) {
      const nw = Number(summary.net_worth) || 0;
      const monthLabel = new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" });
      list.push({
        key: "snapshot-current",
        type: "snapshot",
        title: t("pages.sharehub.snapshot_title", { month: monthLabel }),
        body: t("pages.sharehub.networth_body", { amount: formatMoney(nw, cur) }),
        icon: "calendar",
        tint: "olive",
      });
    }
    return list;
  }, [goals, streak, summary, cur, t]);

  if (loading) {
    return (
      <div className="p-8 lg:p-12">
        <div className="animate-pulse text-[var(--text-muted)]">{t("pages.sharehub.loading_wins")}</div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <PageHeader
        subtitle={t("pages.sharehub.subtitle")}
        title={t("pages.sharehub.title")}
        action={
          <div className="hidden md:flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <Lock size={12} />
            <span>{t("pages.sharehub.private_note")}</span>
          </div>
        }
      />

      <p className="text-sm text-[var(--text-secondary)] max-w-2xl mt-3 leading-relaxed">
        {t("pages.sharehub.intro")}
      </p>

      {wins.length === 0 ? (
        <div className="mt-10">
          <EmptyState
            icon={<Sparkles size={20} />}
            title={t("pages.sharehub.no_wins_title")}
            body={t("pages.sharehub.no_wins_body")}
          />
        </div>
      ) : (
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {wins.map(w => (
            <WinCard
              key={w.key}
              win={w}
              t={t}
              onOpen={() => {
                if (w.type === "goal")     setSelectedGoal(w.data);
                if (w.type === "snapshot") setSnapshotOpen(true);
                if (w.type === "year")     setYearOpen(true);
                if (w.type === "streak") {
                  // Streak has no dedicated modal — reuse the snapshot which
                  // already shows the streak count prominently.
                  setSnapshotOpen(true);
                }
              }}
            />
          ))}
        </div>
      )}

      <Link
        to="/wall"
        className="mt-16 block p-6 rounded-2xl border border-[var(--border-default)] bg-[var(--bg-accent)] hover:border-[var(--accent-primary)] transition-colors group"
        data-testid="sharehub-wall-link"
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
               style={{ background: "rgba(45,90,64,0.10)", color: "#2D5A40" }}>
            <Sprout size={18} />
          </div>
          <div className="flex-1">
            <div className="label-eyebrow mb-1">{t("pages.sharehub.wall_eyebrow")}</div>
            <div className="text-sm text-[var(--text-primary)] font-medium">
              {t("pages.sharehub.wall_headline")}
            </div>
            <div className="text-xs text-[var(--text-secondary)] mt-1">
              {t("pages.sharehub.wall_hint")}
            </div>
          </div>
          <div className="text-xs font-medium text-[var(--accent-primary)] group-hover:underline flex-shrink-0">
            {t("pages.sharehub.view_wall")}
          </div>
        </div>
      </Link>

      {/* Modal reuse — the same components used elsewhere */}
      {selectedGoal && (
        <GoalCompleteModal
          goal={selectedGoal}
          currency={cur}
          onClose={() => setSelectedGoal(null)}
        />
      )}
      <MonthlySnapshotModal
        open={snapshotOpen}
        onClose={() => setSnapshotOpen(false)}
        summary={summary}
        streakCount={streak?.current_streak || 0}
        topInsight={Array.isArray(insights?.insights) && insights.insights.length > 0 ? insights.insights[0] : null}
        currency={cur}
        userName={user?.name}
      />
      <YearInReviewModal
        open={yearOpen}
        onClose={() => setYearOpen(false)}
        summary={summary}
        streak={streak}
        goals={goals}
        cashflow={cashflow}
        currency={cur}
        userName={user?.name}
      />
    </div>
  );
}

function WinCard({ win, onOpen, t }) {
  const IconEl = win.icon === "trophy" ? Trophy
              : win.icon === "flame"  ? Flame
              : win.icon === "star"   ? Star
              : CalendarDays;
  const tints = {
    amber:    { bg: "linear-gradient(135deg, rgba(224,158,80,0.18) 0%, rgba(224,158,80,0.06) 100%)", color: "#A16A25", border: "rgba(224,158,80,0.35)" },
    green:    { bg: "linear-gradient(135deg, rgba(45,90,64,0.14)  0%, rgba(45,90,64,0.04)  100%)", color: "#2D5A40", border: "rgba(45,90,64,0.30)" },
    olive:    { bg: "linear-gradient(135deg, rgba(87,106,94,0.14) 0%, rgba(87,106,94,0.05) 100%)", color: "#4A5D51", border: "rgba(87,106,94,0.30)" },
    gradient: {
      bg: "linear-gradient(135deg, rgba(224,158,80,0.22) 0%, rgba(45,90,64,0.18) 100%)",
      color: "#A16A25",
      border: "rgba(224,158,80,0.45)",
    },
  };
  const tint = tints[win.tint] || tints.green;
  const eyebrow =
    win.type === "goal"     ? t("pages.sharehub.eyebrow_goal")
  : win.type === "streak"   ? t("pages.sharehub.eyebrow_streak")
  : win.type === "year"     ? t("pages.sharehub.eyebrow_year")
  :                           t("pages.sharehub.eyebrow_snapshot");
  return (
    <button
      onClick={onOpen}
      className="card-flat text-left p-5 shadow-soft tilt group"
      style={{ borderColor: tint.border, background: tint.bg }}
      data-testid={`win-card-${win.key}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(255,255,255,0.55)", color: tint.color }}
          >
            <IconEl size={18} strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.22em] font-medium" style={{ color: tint.color }}>
              {eyebrow}
            </div>
            <div className="text-base font-medium leading-tight tracking-tight mt-0.5 truncate">
              {win.title}
            </div>
          </div>
        </div>
        <ChevronRight size={16} className="text-[var(--text-muted)] flex-shrink-0 group-hover:text-[var(--text-primary)] transition-colors" />
      </div>
      <div className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">
        {win.body}
      </div>
      <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--accent-primary)]">
        <Share2 size={12} />
        <span>{t("pages.sharehub.open_share")}</span>
      </div>
    </button>
  );
}
